import { Link } from "../lib/route";
import { ArrowRight, PhoneCall, MapPin } from "lucide-react";
import { richText } from "@/lib/sanitizeHtml";
import { withElStyle } from "../lib/_data";

/**
 * Full-width hero banner with dark overlay + optional dual CTAs.
 * All heroes stretch edge to edge, minimum 70vh on desktop, 60vh on mobile.
 */
export default function Hero({
  image,
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  trustLine,
  minHeight = "min-h-[70vh] md:min-h-[86vh]",
  align = "left",
  showPhone = false,
}) {
  const alignClass =
    align === "center" ? "items-center text-center mx-auto" : "items-start text-left";

  return (
    <section
      {...withElStyle("hero-banner", {
        className: `relative w-full ${minHeight} overflow-hidden grain`,
      })}
      data-testid="hero-banner"
    >
      {image ? (
        <img
          src={image}
          alt=""
          data-testid="hero-image"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
      ) : (
        <div
          data-testid="hero-image"
          className="absolute inset-0 w-full h-full"
          style={{ background: "linear-gradient(135deg, #2c2a29 0%, #4a3d33 60%, #c85a3c 100%)" }}
          aria-hidden="true"
        />
      )}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(44,42,41,0.35) 0%, rgba(44,42,41,0.55) 45%, rgba(44,42,41,0.75) 100%)",
        }}
      />
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14 pt-32 pb-20 md:pt-40 md:pb-28 flex flex-col justify-end">
        <div className={`flex flex-col ${alignClass} max-w-3xl gap-6 rise`}>
          {eyebrow && (
            <div
              {...withElStyle("hero_eyebrow", {
                className: "inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fffdf9]/15 backdrop-blur-md border border-[#fffdf9]/25 text-[#fffdf9] text-xs tracking-[0.22em] uppercase font-semibold",
              })}
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#c85a3c]" />
              <span {...richText(eyebrow)} />
            </div>
          )}
          <h1
            {...withElStyle("hero_title", {
              className: "font-display text-4xl sm:text-5xl lg:text-7xl font-black text-[#fffdf9] leading-[1.02]",
            })}
            data-testid="hero-title"
            {...richText(title)}
          />
          {subtitle && (
            <p
              {...withElStyle("hero_subtitle", {
                className: "text-base md:text-lg text-[#f5efe4] max-w-2xl leading-relaxed",
              })}
              data-testid="hero-subtitle"
              {...richText(subtitle)}
            />
          )}

          {(primaryCta || secondaryCta) && (
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              {primaryCta && (
                <Link
                  to={primaryCta.to}
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#c85a3c] hover:bg-[#a64b32] text-[#fffdf9] font-bold px-7 py-4 rounded-full text-base shadow-[0_10px_28px_rgba(200,90,60,0.45)] transition-all hover:-translate-y-0.5"
                >
                  {primaryCta.label}
                  <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </Link>
              )}
              {secondaryCta && (
                <Link
                  to={secondaryCta.to}
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#fffdf9] text-[#fffdf9] hover:bg-[#fffdf9] hover:text-[#2c2a29] font-bold px-7 py-4 rounded-full text-base transition-colors"
                >
                  {secondaryCta.label}
                </Link>
              )}
              {showPhone && (
                <a
                  href="tel:+15552184400"
                  data-testid="hero-call-cta"
                  className="inline-flex items-center justify-center gap-2 text-[#fffdf9] font-bold px-4 py-4 hover:text-[#c85a3c] transition-colors"
                >
                  <PhoneCall className="w-4 h-4" />
                  or call (555) 218-4400
                </a>
              )}
            </div>
          )}

          {trustLine && (
            <div
              data-testid="hero-trust-line"
              className="mt-6 flex items-center gap-2 text-sm text-[#f5efe4]/85"
            >
              <MapPin className="w-4 h-4 text-[#c85a3c]" />
              {trustLine}
            </div>
          )}
        </div>
      </div>

      {/* soft bottom fade into page */}
      <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-b from-transparent to-[#f9f6f0] z-10 pointer-events-none" />
    </section>
  );
}
